import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  makeStyles,
  Switch,
  Grid,
  Typography,
  Divider,
  Button,
  Box,
  TextField,
  Checkbox
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { isBulkPackagingType } from 'utils/packagingTypeBehavior';
import {
  shouldPrintFourAutomationLabelsPerPak,
  shouldShowIncomingDeliveryReference,
} from 'utils/tenantFeatures';
import { styles } from './../styles';
import moment from 'moment';

const CALL_OFF_REGEX = /^[A-Za-z0-9]{10}$/;

const defaultState = {
  warehouseId: '',
  packagingTypeId: '',
  packagingQuantity: '',
  quantity: '',
  productId: '',
  nextProcessTypeId: '',
  deliveryType: '',
  supplierId: '',
  supplierName: '',
  supplierCode: '',
  callOff: '',
  incomingDeliveryReference: '',
  plateNumber: '',
}

const getGenericWarehouseId = (warehouses = {}) => {
  const candidates = ['generica', 'genirica', 'generic'];

  const match = Object.values(warehouses).find((warehouse) => {
    const rawName = `${warehouse?.name || ''}`.trim().toLowerCase();
    const normalizedName = rawName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return candidates.some((candidate) => normalizedName === candidate || normalizedName.includes(candidate));
  });

  return match?.id || null;
};

const getDefaultNextProcessTypeId = (processTypes = {}) => {
  const candidates = ['calidad inicial', 'initial quality'];

  const match = Object.values(processTypes).find((processType) => {
    const rawName = `${processType?.name || ''}`.trim().toLowerCase();
    const normalizedName = rawName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return candidates.some(
      (candidate) =>
        normalizedName === candidate || normalizedName.includes(candidate),
    );
  });

  return match?.id || null;
};

const ReceiptsModal = ({
  open,
  onClose,
  products = {},
  processTypes = {},
  warehouses = {},
  packagingTypes = {},
  actions,
  accounts,
  suppliers = {},
}) => {
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const shouldRenderIncomingDeliveryReference =
    shouldShowIncomingDeliveryReference();
  const genericWarehouseId = useMemo(() => getGenericWarehouseId(warehouses), [warehouses]);
  const defaultNextProcessTypeId = useMemo(
    () => getDefaultNextProcessTypeId(processTypes),
    [processTypes],
  );

  const [formData, setFormData] = useState({
    autoFillOutputProduct: true,
    autoCreateProcess: true,
    autoCreateInboundPak: true,
    productId: null,
    nextProcessTypeId: defaultNextProcessTypeId,
    warehouseId: genericWarehouseId,
    packagingTypeId: null,
    packagingQuantity: '1',
    quantity: '',
    splitPaks: false,
    hasPallet: false,
    deliveryType: 'Normal',
    supplierId: null,
    supplierName: null,
    supplierCode: null,
    callOff: '',
    incomingDeliveryReference: '',
    plateNumber: '',
  });

  const [errors, setErrors] = useState(defaultState);

  const handleProductChange = (_, selectedProduct) => {
    setFormData(prev => ({
      ...prev,
      productId: selectedProduct?.id || null
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.productId) {
      newErrors.productId = t('processTypes.receipts_modal.errors.code');
    }
    if (!formData.warehouseId) {
      newErrors.warehouseId = t('processTypes.receipts_modal.errors.store');
    }
    if (!formData.packagingTypeId) {
      newErrors.packagingTypeId = t('processTypes.receipts_modal.errors.packaging');
    }
    if (!formData.packagingQuantity || Number(formData.packagingQuantity) <= 0) {
      newErrors.packagingQuantity = t('processTypes.receipts_modal.errors.number');
    }
    if (formData.autoCreateProcess && !formData.nextProcessTypeId) {
      newErrors.nextProcessTypeId = t('processTypes.receipts_modal.errors.autoCreateProcess');
    }
    if (!CALL_OFF_REGEX.test((formData.callOff || '').trim())) {
      newErrors.callOff = t('processTypes.receipts_modal.errors.callOffFormat');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const now = new Date();
    const currentDate = now.toISOString();

    const account = Object.values(accounts || {}).find(
      (acc) => acc.companyName === 'AGRICHILE'
    );

    const selectedPackaging = packagingTypes[formData.packagingTypeId];

    const payload = {
      ...formData,
      hasAutomation: true,
      accountId: account?.id,
      date: currentDate,
      harvestDate: currentDate,
      internalClientId: null,
      packagingTypeName: selectedPackaging?.name || '',
      customFields: {
        deliveryType: formData.deliveryType,
        supplierName: formData.supplierName,
        supplierCode: formData.supplierCode,
        callOff: formData.callOff,
        incomingDeliveryReference: formData.incomingDeliveryReference,
        plateNumber: formData.plateNumber,
      },
    };

    actions.createReceiptTrigger(payload);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setFormData({
        autoFillOutputProduct: true,
        autoCreateProcess: true,
        autoCreateInboundPak: true,
        productId: null,
        nextProcessTypeId: defaultNextProcessTypeId,
        warehouseId: genericWarehouseId,
        packagingTypeId: null,
        packagingQuantity: '1',
        quantity: '',
        splitPaks: false,
        hasPallet: false,
        deliveryType: 'Normal',
        supplierId: null,
        supplierName: null,
        supplierCode: null,
        callOff: '',
        incomingDeliveryReference: '',
        plateNumber: '',
      });
      setErrors(defaultState);
    }
  }, [open, genericWarehouseId, defaultNextProcessTypeId]);

  useEffect(() => {
    if (open && !formData.warehouseId && genericWarehouseId) {
      setFormData((prev) => ({ ...prev, warehouseId: genericWarehouseId }));
    }
  }, [open, formData.warehouseId, genericWarehouseId]);

  useEffect(() => {
    if (
      open &&
      formData.autoCreateProcess &&
      !formData.nextProcessTypeId &&
      defaultNextProcessTypeId
    ) {
      setFormData((prev) => ({
        ...prev,
        nextProcessTypeId: defaultNextProcessTypeId,
      }));
    }
  }, [
    open,
    formData.autoCreateProcess,
    formData.nextProcessTypeId,
    defaultNextProcessTypeId,
  ]);

  const currentProduct = products[formData.productId];
  const selectedPackagingType = packagingTypes[formData.packagingTypeId] || null;
  const usesBulkPackaging = useMemo(
    () => isBulkPackagingType(selectedPackagingType),
    [selectedPackagingType],
  );

  useEffect(() => {
    if (usesBulkPackaging && formData.splitPaks) {
      setFormData((prev) => ({
        ...prev,
        splitPaks: false,
      }));
    }
  }, [usesBulkPackaging, formData.splitPaks]);

  const handleSupplierChange = (_, val) => setFormData({
    ...formData,
    supplierId: val?.id || null,
    supplierName: val?.Entity?.name || '',
    supplierCode: val?.code || ''
  });
  const handleWarehouseChange = (_, val) => setFormData({ ...formData, warehouseId: val?.id || null });
  const handlePackagingTypeChange = (_, val) => setFormData({ ...formData, packagingTypeId: val?.id || null });
  const handleNextProcessTypeChange = (_, val) => setFormData({ ...formData, nextProcessTypeId: val?.id || null });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('processTypes.receipts_modal.title')}
        <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>{t('processTypes.receipts_modal.product_form.title')}</Typography>

        <Grid container spacing={2} style={{ marginBottom: '1.5rem' }}>
          <Grid item xs={12}>
            <Autocomplete
              options={Object.values(products || {})}
              getOptionLabel={(o) => o?.name}
              value={currentProduct || null}
              onChange={handleProductChange}
              renderInput={(params) => <TextField {...params} label={t('processTypes.receipts_modal.product_form.labels.product')} />}
            />
            {errors.productId && <Typography color="error" variant="caption">{errors.productId}</Typography>}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t('processTypes.receipts_modal.product_form.labels.specie')}
              value={currentProduct?.Variety?.Fruit?.name || ""}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t('processTypes.receipts_modal.product_form.labels.variety')}
              value={currentProduct?.Variety?.name || ""}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t('processTypes.receipts_modal.product_form.labels.product')}
              value={currentProduct?.name || ""}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" gutterBottom>{t('processTypes.receipts_modal.packaging_form.title')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={Object.values(warehouses)}
              getOptionLabel={(o) => o.name || ""}
              value={warehouses[formData.warehouseId] || null}
              onChange={handleWarehouseChange}
              renderInput={(params) => <TextField {...params} label={t('processTypes.receipts_modal.packaging_form.labels.warehouse')} />}
            />
            {errors.warehouseId && <Typography color="error" variant="caption">{errors.warehouseId}</Typography>}
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={Object.values(packagingTypes || {})}
              getOptionLabel={(o) => o.name || ""}
              value={packagingTypes[formData.packagingTypeId] || null}
              onChange={handlePackagingTypeChange}
              renderInput={(params) => <TextField {...params} label={t('processTypes.receipts_modal.packaging_form.labels.packaging')} />}
            />
            {errors.packagingTypeId && <Typography color="error" variant="caption">{errors.packagingTypeId}</Typography>}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label={t('processTypes.receipts_modal.packaging_form.labels.number')}
              value={formData.packagingQuantity}
              onChange={(e) => setFormData({ ...formData, packagingQuantity: e.target.value })}
            />
            {errors.packagingQuantity && <Typography color="error" variant="caption">{errors.packagingQuantity}</Typography>}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label={t('processTypes.receipts_modal.packaging_form.labels.quantity')}
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            />
            {errors.quantity && <Typography color="error" variant="caption">{errors.quantity}</Typography>}
          </Grid>

          <Grid item xs={6}>
            <FormControlLabel
              control={<Checkbox checked={formData.splitPaks} onChange={(e) => setFormData({ ...formData, splitPaks: e.target.checked })} color="primary" disabled={usesBulkPackaging} />}
              label={t('processTypes.receipts_modal.checkboxes_form.split_paks')}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={<Checkbox checked={formData.hasPallet} onChange={(e) => setFormData({ ...formData, hasPallet: e.target.checked })} color="primary" />}
              label={t('processTypes.receipts_modal.checkboxes_form.has_pallet')}
            />
          </Grid>
          {usesBulkPackaging && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                {'Para tolva/granel se crea un solo PAK por defecto; el peso no define múltiples etiquetas.'}
              </Typography>
            </Grid>
          )}
          {shouldPrintFourAutomationLabelsPerPak() && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                {t('receipts.form.automationCopiesHint')}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Divider style={{ margin: '16px 0' }} />

        <Typography variant="subtitle1" gutterBottom>{t('processTypes.receipts_modal.additional_info.title')}</Typography>

        <Box display="flex" flexDirection="column">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={['Normal', 'Directa']}
                value={formData.deliveryType || null}
                onChange={(_, val) => setFormData({ ...formData, deliveryType: val || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={t('processTypes.receipts_modal.additional_info.labels.delivery_type')}
                    error={!!errors.deliveryType}
                    helperText={errors.deliveryType}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={Object.values(suppliers)}
                getOptionLabel={(o) => {
                  const name = o.Entity?.name || '';
                  return o.code ? `${name} (${o.code})` : name;
                }}
                value={Object.values(suppliers).find((s) => s.id === formData.supplierId) || null}
                onChange={handleSupplierChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('processTypes.receipts_modal.additional_info.labels.supplier_code')}
                  />
                )}
              />
              {errors.supplierId && <Typography color="error" variant="caption">Seleccione un proveedor</Typography>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('processTypes.receipts_modal.additional_info.labels.call_off')}
                value={formData.callOff}
                onChange={(e) => {
                  setFormData({ ...formData, callOff: e.target.value || '' });
                }}
                inputProps={{ maxLength: 10 }}
              />
              {errors.callOff && <Typography color="error" variant="caption">{errors.callOff}</Typography>}
            </Grid>

            {shouldRenderIncomingDeliveryReference && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('processTypes.receipts_modal.additional_info.labels.incoming_delivery_reference')}
                  value={formData.incomingDeliveryReference}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      incomingDeliveryReference: e.target.value || '',
                    });
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('processTypes.receipts_modal.additional_info.labels.plate_number')}
                value={formData.plateNumber}
                onChange={(e) => {
                  const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

                  const match = raw.match(/^([A-Z]{0,4})([0-9]{0,4})$/);
                  if (match) {
                    const letters = match[1].slice(0, 4);
                    const numbers = match[2].slice(0, letters.length <= 2 ? 4 : 2);
                    setFormData({ ...formData, plateNumber: letters + numbers });
                  }
                }}
                inputProps={{ maxLength: 6 }}
                placeholder="XX1111 / XXZZ22"
              />
              {errors.plateNumber && <Typography color="error" variant="caption">{errors.plateNumber}</Typography>}
            </Grid>
          </Grid>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <Box display="flex" flexDirection="column">
          <FormControlLabel
            classes={{ root: classes.switchContainer, label: classes.switchLabel }}
            labelPlacement="start"
            control={<Switch checked={formData.autoFillOutputProduct} onChange={(e) => setFormData({ ...formData, autoFillOutputProduct: e.target.checked })} color="primary" />}
            label={t('processTypes.receipts_modal.switches_form.labels.no_reenter')}
          />

          <FormControlLabel
            classes={{ root: classes.switchContainer, label: classes.switchLabel }}
            labelPlacement="start"
            control={
              <Switch
                checked={formData.autoCreateProcess}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setFormData({
                    ...formData,
                    autoCreateProcess: isChecked,
                    nextProcessTypeId:
                      isChecked
                        ? formData.nextProcessTypeId || defaultNextProcessTypeId
                        : null,
                  });
                }}
                color="primary"
              />
            }
            label={t('processTypes.receipts_modal.switches_form.labels.autocreate_process')}
          />

          {formData.autoCreateProcess && (
            <Grid item xs={12} sm={6} style={{ margin: "1rem 0" }}>
              <Autocomplete
                options={Object.values(processTypes || {})}
                getOptionLabel={(o) => o.name || ""}
                value={processTypes[formData.nextProcessTypeId] || null}
                onChange={handleNextProcessTypeChange}
                renderInput={(params) => (
                  <TextField {...params} label={t('processTypes.receipts_modal.switches_form.labels.next_process')} error={!!errors.nextProcessTypeId} helperText={errors.nextProcessTypeId} />
                )}
              />
            </Grid>
          )}

          <FormControlLabel
            classes={{ root: classes.switchContainer, label: classes.switchLabel }}
            labelPlacement="start"
            control={<Switch checked={formData.autoCreateInboundPak} onChange={(e) => setFormData({ ...formData, autoCreateInboundPak: e.target.checked })} color="primary" />}
            label={t('processTypes.receipts_modal.switches_form.labels.autocreate_outbound_pak')}
          />
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end" pb={2}>
          <Button onClick={onClose} color="primary" style={{ marginRight: 8 }}>{t('CANCELAR')}</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>{t('AGREGAR')}</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptsModal;
